import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Launch from '../images/Launch.jpeg';

const AITrainerKitAdvantages = () => {
  const [activeFeature, setActiveFeature] = useState(3); // Default to feature 4

  const features = [
    { id: 0, title: "Portable AI Trainer Device – Powered By Intel", description: "Experience cutting-edge AI learning with Intel-powered hardware designed for maximum portability and performance." },
    { id: 1, title: "Plug & Play Simplicity", description: "Get started instantly with zero configuration required. Simply connect the device and begin your AI learning journey." },
    { id: 2, title: "Global Curriculum Framework", description: "Aligned with international educational standards including NEP 2020, UNESCO guidelines, and CSTA frameworks." },
    { id: 3, title: "Hands-On Projects & Prototypes", description: "Build and train real-world AI models with visual, audio, and sensor-based applications." }
  ];

  const handleFeatureClick = (featureId) => {
    setActiveFeature(activeFeature === featureId ? null : featureId);
  };
  
  // Base styles for the component
  const styles = {
    container: {
      backgroundColor: '#0a0a0a',
      color: '#ffffff',
      padding: '6rem 2rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    contentWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '4rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      margin: '0',
      lineHeight: '1.2'
    },
    titleUnderline: {
      width: '100px',
      height: '3px',
      backgroundColor: '#6366f1',
      margin: '2rem auto 0',
      borderRadius: '2px'
    },
    mainLayout: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '4rem'
    },
    imageContainer: {
      flex: '1',
      position: 'sticky',
      top: '2rem'
    },
    imageWrapper: {
      minHeight: '400px',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
    },
    image: {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '20px'
    },
    featuresContainer: {
      flex: '1.2',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    featureCard: {
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    featureHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    featureTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      flex: '1'
    },
    featureIcon: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      marginRight: '1rem',
      minWidth: '24px'
    },
    featureTitle: {
      lineHeight: '1.4'
    },
    featureDescriptionContainer: {
      animation: 'fadeIn 0.4s ease-in-out'
    },
    featureDescription: {
      fontSize: '1rem',
      margin: '0',
      lineHeight: '1.6',
      opacity: '0.95',
      paddingLeft: '2.5rem'
    }
  };

  return (
    <div style={styles.container} data-component="container">
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 0.95; transform: translateY(0); }
          }
          
          /* Hover effect for non-active cards */
          [data-component="feature-card"]:hover {
            border-color: #6366f1 !important;
            transform: translateY(-2px) !important;
          }
          
          /* Responsive Layout */
          @media (max-width: 992px) {
            [data-component="responsive-container"] {
              flex-direction: column !important;
              gap: 3rem !important;
            }
            [data-component="image-container"] {
              position: static !important; /* Disable sticky positioning */
              width: 100%;
            }
          }
          
          @media (max-width: 768px) {
            [data-component="container"] {
              padding: 4rem 1rem !important;
            }
            [data-component="title"] {
              font-size: 2rem !important;
            }
            [data-component="feature-card"] {
              padding: 1.5rem !important;
            }
          }
        `}
      </style>
      
      <div style={styles.contentWrapper}>
        <div style={styles.header}>
          <h1 style={styles.title} data-component="title">
            Advantage of <span style={{ color: '#6366f1' }}>AI Trainer Kit</span>
          </h1>
          <div style={styles.titleUnderline}></div>
        </div>

        <div style={styles.mainLayout} data-component="responsive-container">
          
          <div style={styles.imageContainer} data-component="image-container">
            <div style={styles.imageWrapper}>
                <img src={Launch} alt="AI Trainer Kit Launch" style={styles.image} />
            </div>
          </div>

          <div style={styles.featuresContainer}>
            {features.map((feature) => {
              const isActive = activeFeature === feature.id;
              return (
                <div
                  key={feature.id}
                  onClick={() => handleFeatureClick(feature.id)}
                  data-component="feature-card"
                  style={{
                    ...styles.featureCard,
                    padding: isActive ? '2rem' : '1.5rem',
                    backgroundColor: isActive ? '#6366f1' : '#1a1a1a',
                    border: isActive ? 'none' : '1px solid #2a2a2a',
                    borderRadius: '16px',
                    boxShadow: isActive ? '0 10px 30px rgba(99, 102, 241, 0.3)' : '0 4px 10px rgba(0,0,0,0.1)',
                    transform: isActive ? 'translateY(-4px)' : 'translateY(0)'
                  }}
                >
                  <div style={{...styles.featureHeader, marginBottom: isActive ? '1rem' : '0'}}>
                    <div style={styles.featureTitleContainer}>
                      <div style={{...styles.featureIcon, color: isActive ? '#ffffff' : '#6366f1'}}>+</div>
                      <span style={{...styles.featureTitle, fontSize: isActive ? '1.2rem' : '1.1rem', color: '#ffffff', fontWeight: isActive ? '600' : '500'}}>
                        {feature.title}
                      </span>
                    </div>
                    <div style={{color: isActive ? '#ffffff' : '#6366f1', marginLeft: '1rem'}}>
                      {isActive ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                  
                  {isActive && (
                    <div style={styles.featureDescriptionContainer}>
                      <p style={styles.featureDescription}>{feature.description}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITrainerKitAdvantages;